import apiClient, { ApiResponse, PaginatedResponse } from '../api/client';
import { Service } from '../types/models';

export interface ServiceFilters {
  search?: string;
  category_id?: number;
  billing_type?: 'periodical' | 'one_off';
  page?: number;
  per_page?: number;
}

export interface CreateServiceData {
  name: string;
  service_code?: string;
  description?: string;
  category_id: number;
  billing_type: 'periodical' | 'one_off';
  billing_cycle_id?: number;
  icon?: File;
}

export interface UpdateServiceData extends Partial<CreateServiceData> {}

class ServiceService {
  private baseUrl = '/admin/county/modules';

  // Get all services for a module
  async getServices(moduleId: number, filters?: ServiceFilters): Promise<ApiResponse<PaginatedResponse<Service>>> {
    const params = new URLSearchParams();
    if (filters?.search) params.append('search', filters.search);
    if (filters?.category_id) params.append('category_id', filters.category_id.toString());
    if (filters?.billing_type) params.append('billing_type', filters.billing_type);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.per_page) params.append('per_page', filters.per_page.toString());

    return apiClient.get<PaginatedResponse<Service>>(`${this.baseUrl}/${moduleId}/services?${params.toString()}`);
  }

  // Get single service
  async getService(moduleId: number, serviceId: number): Promise<ApiResponse<Service>> {
    return apiClient.get<Service>(`${this.baseUrl}/${moduleId}/services/${serviceId}`);
  }

  // Create service
  async createService(moduleId: number, data: CreateServiceData): Promise<ApiResponse<Service>> {
    if (data.icon) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });
      return apiClient.upload<Service>(`${this.baseUrl}/${moduleId}/services`, formData);
    }

    return apiClient.post<Service>(`${this.baseUrl}/${moduleId}/services`, data);
  }

  // Update service
  async updateService(moduleId: number, serviceId: number, data: UpdateServiceData): Promise<ApiResponse<Service>> {
    if (data.icon) {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, String(value));
          }
        }
      });
      formData.append('_method', 'PUT');
      return apiClient.upload<Service>(`${this.baseUrl}/${moduleId}/services/${serviceId}`, formData);
    }

    return apiClient.put<Service>(`${this.baseUrl}/${moduleId}/services/${serviceId}`, data);
  }

  // Delete service
  async deleteService(moduleId: number, serviceId: number): Promise<ApiResponse> {
    return apiClient.delete(`${this.baseUrl}/${moduleId}/services/${serviceId}`);
  }

  // Search services
  async searchServices(moduleId: number, query: string): Promise<ApiResponse<Service[]>> {
    return apiClient.get<Service[]>(`${this.baseUrl}/${moduleId}/services/search?q=${query}`);
  }
}

export const serviceService = new ServiceService();
export default serviceService;
